import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Mail, Phone, MessageSquare, ChevronRight, Check, Loader2 } from 'lucide-react';
import { getServices, groupServicesByCategory, createBooking } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Calendar as CalendarComponent } from './ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

const Booking = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: null,
    time: '',
    message: '',
  });
  const [services, setServices] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      const data = await getServices();
      const grouped = groupServicesByCategory(data);
      setServices(grouped);
    };
    fetchServices();
  }, []);

  const timeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '14:00', '14:30', '15:00', '15:30', '16:00',
    '16:30', '17:00', '17:30', '18:00', '18:30'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    // Format date for API
    const bookingData = {
      ...formData,
      date: formData.date ? format(formData.date, 'yyyy-MM-dd') : '',
    };
    
    const result = await createBooking(bookingData);
    
    if (result.success) {
      setIsSubmitted(true);
    } else {
      setError(result.error);
    }
    
    setIsLoading(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      date: null,
      time: '',
      message: '',
    });
    setIsSubmitted(false);
    setError(null);
  };

  return (
    <section id="marcacoes" className="py-24 bg-gradient-to-b from-[#0d0d0d] to-[#0a0a0a] relative">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(193,165,79,0.03)_0%,_transparent_60%)]" />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#E5B1C4] text-sm tracking-[0.3em] uppercase mb-4 block">
            Agende a Sua Sessão
          </span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl text-gold-gradient mb-6">
            Marcações Online
          </h2>
          <div className="flex items-center justify-center mb-6">
            <div className="w-24 h-px bg-gradient-to-r from-transparent to-[#C1A54F]" />
            <span className="mx-4 text-[#E1C57C]">✦</span>
            <div className="w-24 h-px bg-gradient-to-l from-transparent to-[#C1A54F]" />
          </div>
          <p className="text-[#bbb5ae] max-w-xl mx-auto">
            Preencha o formulário abaixo e entraremos em contacto para confirmar a sua marcação
          </p>
        </div>

        {/* Success Message */}
        {isSubmitted ? (
          <div className="text-center py-16 bg-[#111111] border border-[#C1A54F]/20 rounded-sm">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#C1A54F]/10 flex items-center justify-center">
              <Check className="text-[#E1C57C]" size={40} />
            </div>
            <h3 className="font-display text-3xl text-[#f5f0e8] mb-4">
              Pedido Enviado com Sucesso!
            </h3>
            <p className="text-[#bbb5ae] mb-8 max-w-md mx-auto">
              Obrigado pelo seu pedido de marcação. Entraremos em contacto em breve para confirmar a sua sessão.
            </p>
            <Button
              onClick={resetForm}
              className="btn-outline-gold px-8 py-3 rounded-none"
            >
              Fazer Nova Marcação
            </Button>
          </div>
        ) : (
          /* Booking Form */
          <form onSubmit={handleSubmit} className="bg-[#111111] border border-[#C1A54F]/10 rounded-sm p-8 md:p-12">
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-sm text-red-400 text-sm">
                {error}
              </div>
            )}
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Name */}
              <div className="space-y-2">
                <label className="text-[#f5f0e8] text-sm flex items-center">
                  <User size={14} className="mr-2 text-[#E1C57C]" />
                  Nome Completo *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="form-input w-full px-4 py-3 rounded-none bg-[#0a0a0a] border-[#C1A54F]/20"
                  placeholder="O seu nome"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <label className="text-[#f5f0e8] text-sm flex items-center">
                  <Mail size={14} className="mr-2 text-[#E1C57C]" />
                  Email *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="form-input w-full px-4 py-3 rounded-none bg-[#0a0a0a] border-[#C1A54F]/20"
                  placeholder="seu@email.com"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <label className="text-[#f5f0e8] text-sm flex items-center">
                  <Phone size={14} className="mr-2 text-[#E1C57C]" />
                  Telefone *
                </label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="form-input w-full px-4 py-3 rounded-none bg-[#0a0a0a] border-[#C1A54F]/20"
                  placeholder="+351 xxx xxx xxx"
                />
              </div>

              {/* Service */}
              <div className="space-y-2">
                <label className="text-[#f5f0e8] text-sm flex items-center">
                  <ChevronRight size={14} className="mr-2 text-[#E1C57C]" />
                  Serviço *
                </label>
                <Select
                  value={formData.service}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, service: value }))}
                >
                  <SelectTrigger className="form-input w-full px-4 py-3 rounded-none bg-[#0a0a0a] border-[#C1A54F]/20 h-auto">
                    <SelectValue placeholder="Selecione um serviço" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111111] border-[#C1A54F]/20">
                    {services.map((cat, catIndex) => (
                      <React.Fragment key={catIndex}>
                        <div className="px-3 py-2 text-[#E1C57C] text-xs uppercase tracking-wider">
                          {cat.category}
                        </div>
                        {cat.items.map((item, index) => (
                          <SelectItem
                            key={`${catIndex}-${index}`}
                            value={item.name}
                            className="text-[#f5f0e8] hover:bg-[#C1A54F]/10 cursor-pointer"
                          >
                            {item.name}
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date */}
              <div className="space-y-2">
                <label className="text-[#f5f0e8] text-sm flex items-center">
                  <Calendar size={14} className="mr-2 text-[#E1C57C]" />
                  Data Pretendida *
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="form-input w-full px-4 py-3 rounded-none bg-[#0a0a0a] border-[#C1A54F]/20 justify-start text-left font-normal h-auto"
                    >
                      {formData.date ? (
                        format(formData.date, "PPP", { locale: pt })
                      ) : (
                        <span className="text-[#736c64]">Selecione uma data</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#111111] border-[#C1A54F]/20" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={formData.date}
                      onSelect={(date) => setFormData(prev => ({ ...prev, date }))}
                      disabled={(date) => date < new Date() || date.getDay() === 0}
                      initialFocus
                      className="bg-[#111111]"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time */}
              <div className="space-y-2">
                <label className="text-[#f5f0e8] text-sm flex items-center">
                  <Clock size={14} className="mr-2 text-[#E1C57C]" />
                  Hora Pretendida *
                </label>
                <Select
                  value={formData.time}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, time: value }))}
                >
                  <SelectTrigger className="form-input w-full px-4 py-3 rounded-none bg-[#0a0a0a] border-[#C1A54F]/20 h-auto">
                    <SelectValue placeholder="Selecione uma hora" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111111] border-[#C1A54F]/20">
                    {timeSlots.map((time) => (
                      <SelectItem
                        key={time}
                        value={time}
                        className="text-[#f5f0e8] hover:bg-[#C1A54F]/10 cursor-pointer"
                      >
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div className="space-y-2 md:col-span-2">
                <label className="text-[#f5f0e8] text-sm flex items-center">
                  <MessageSquare size={14} className="mr-2 text-[#E1C57C]" />
                  Mensagem (opcional)
                </label>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="form-input w-full px-4 py-3 rounded-none bg-[#0a0a0a] border-[#C1A54F]/20 min-h-[120px] resize-none"
                  placeholder="Alguma informação adicional que gostaria de partilhar..."
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8 text-center">
              <Button
                type="submit"
                disabled={isLoading}
                className="btn-gold px-12 py-4 rounded-none font-medium tracking-widest text-sm w-full sm:w-auto"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                    A ENVIAR...
                  </span>
                ) : (
                  'ENVIAR PEDIDO DE MARCAÇÃO'
                )}
              </Button>
              <p className="text-[#736c64] text-xs mt-4">
                * Campos obrigatórios. Entraremos em contacto para confirmar a disponibilidade.
              </p>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default Booking;
